import React, { useState, useEffect } from 'react';
import {
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Button,
    Typography,
    CircularProgress,
    Tooltip,
    Checkbox,
    ListItemText,
    Paper,
    Stack,
    Divider,
    Radio,
    RadioGroup,
    FormControlLabel,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { useTranslation } from 'react-i18next';
import useNotify from '../../hooks/useNotify';
import { useGetAffiliateAgentReport } from '@openapi/reports/reports';
import AffiliateAgentReportTable from '../reports/components/AffiliateAgentReportTable';
import { exportAffiliateAgentReportToXlsx } from '../reports/helpers/exportAffiliateAgentReport';
import { appendDateRangeParams } from '../../helpers/dateParams';

const BOOKING_STATUSES = ['processing', 'finished', 'cancelled'];

// Keep the shrunk (floating) label flush-left instead of the default outlined
// indent, matching the main Reports page sidebar.
const SHRINK_LABEL_SX = { '&.MuiInputLabel-shrink': { transform: 'scale(0.75) !important' } };

// The Agent Performance report rendered inside the Affiliates management page,
// scoped to the commission group selected at the top of the page (the show is
// pinned by that group, so no separate show filter). The endpoint confirms the
// caller manages the group (or is a superadmin / report-cap holder).
const AgentPerformanceTab = ({ venueId, commissionGroupId }) => {
    const { t } = useTranslation();
    const { notifyError, notifySuccess } = useNotify();

    const [bookingStatuses, setBookingStatuses] = useState(['finished']);
    const [showDay, setShowDay] = useState('');
    const [showDateFrom, setShowDateFrom] = useState('');
    const [showDateTo, setShowDateTo] = useState('');
    const [bookingDay, setBookingDay] = useState('');
    const [bookingDateFrom, setBookingDateFrom] = useState('');
    const [bookingDateTo, setBookingDateTo] = useState('');
    const [bookingDateMode, setBookingDateMode] = useState('created');
    // null until the first fetch; the snapshot of filters the table reflects, so
    // editing a filter doesn't refetch until Apply. The commission group comes
    // from the page selector and applies immediately.
    const [appliedParams, setAppliedParams] = useState(null);

    const effectiveShowFrom = showDay || showDateFrom;
    const effectiveShowTo = showDay || showDateTo;
    const effectiveBookingFrom = bookingDay || bookingDateFrom;
    const effectiveBookingTo = bookingDay || bookingDateTo;

    const buildParams = () => {
        const params = {
            bookingStatuses: bookingStatuses.length > 0 ? bookingStatuses : undefined,
        };
        appendDateRangeParams(params, [
            ['showDateFrom', effectiveShowFrom],
            ['showDateTo', effectiveShowTo],
            ['bookingDateFrom', effectiveBookingFrom],
            ['bookingDateTo', effectiveBookingTo],
        ]);
        if (effectiveBookingFrom || effectiveBookingTo) {
            params.bookingDateMode = bookingDateMode;
        }
        return params;
    };

    // Load the default view as soon as the venue is known.
    useEffect(() => {
        if (venueId && appliedParams === null) {
            setAppliedParams(buildParams());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [venueId]);

    // commissionGroupId is part of the query params (not the snapshot), so
    // switching the group at the top refetches immediately.
    const { data: reportData, isLoading } = useGetAffiliateAgentReport(
        Number(venueId),
        { commissionGroupId: commissionGroupId ?? undefined, ...(appliedParams || {}) },
        {
            query: {
                enabled: !!venueId && !!commissionGroupId && appliedParams !== null,
                onError: notifyError,
            },
        },
    );

    const hasData = (reportData?.items?.length ?? 0) > 0;
    const dateFrom = effectiveShowFrom || effectiveBookingFrom;
    const dateTo = effectiveShowTo || effectiveBookingTo;

    const handleApply = () => setAppliedParams(buildParams());

    const handleExport = () => {
        if (!hasData) return;
        exportAffiliateAgentReportToXlsx(reportData, dateFrom, dateTo);
        notifySuccess(t('reports.exportSuccess'));
    };

    const handleStatusChange = (event) => {
        const value = event.target.value;
        setBookingStatuses(typeof value === 'string' ? value.split(',') : value);
    };
    const getStatusLabel = (selected) =>
        (selected || []).map((s) => t(`bookings.columns.${s}`, s)).join(', ');

    const pickShowDay = (value) => {
        setShowDay(value);
        if (value) {
            setShowDateFrom('');
            setShowDateTo('');
        }
    };
    const pickBookingDay = (value) => {
        setBookingDay(value);
        if (value) {
            setBookingDateFrom('');
            setBookingDateTo('');
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 3,
                width: '100%',
                alignItems: { xs: 'stretch', md: 'flex-start' },
            }}
        >
            <Paper
                elevation={0}
                sx={{
                    width: { xs: '100%', md: 280 },
                    flexShrink: 0,
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    height: 'fit-content',
                    position: { xs: 'static', md: 'sticky' },
                    top: 80,
                    textAlign: 'left',
                }}
            >
                <Stack spacing={2.5} alignItems="stretch">
                    <FormControl fullWidth size="small">
                        <InputLabel id="report-status-label" sx={SHRINK_LABEL_SX}>
                            {t('reports.filters.bookingStatus')}
                        </InputLabel>
                        <Select
                            labelId="report-status-label"
                            multiple
                            value={bookingStatuses}
                            onChange={handleStatusChange}
                            label={t('reports.filters.bookingStatus')}
                            renderValue={getStatusLabel}
                        >
                            {BOOKING_STATUSES.map((status) => (
                                <MenuItem key={status} value={status}>
                                    <Checkbox
                                        checked={bookingStatuses.includes(status)}
                                        size="small"
                                    />
                                    <ListItemText
                                        primary={t(`bookings.columns.${status}`, status)}
                                    />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Divider />

                    <Typography
                        variant="caption"
                        color="text.primary"
                        sx={{ mb: -1, textAlign: 'left', width: '100%', fontWeight: 600 }}
                    >
                        {t('reports.filters.showDateFrom').replace(' From', '')}
                    </Typography>
                    <TextField
                        type="date"
                        label={t('reports.filters.day')}
                        value={showDay}
                        onChange={(e) => pickShowDay(e.target.value)}
                        size="small"
                        fullWidth
                        disabled={!!showDateFrom || !!showDateTo}
                        slotProps={{ inputLabel: { shrink: true } }}
                    />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField
                            type="date"
                            label={t('reports.filters.dateFrom')}
                            value={showDateFrom}
                            onChange={(e) => {
                                setShowDateFrom(e.target.value);
                                if (e.target.value) setShowDay('');
                            }}
                            size="small"
                            fullWidth
                            disabled={!!showDay}
                            slotProps={{ inputLabel: { shrink: true } }}
                        />
                        <TextField
                            type="date"
                            label={t('reports.filters.dateTo')}
                            value={showDateTo}
                            onChange={(e) => {
                                setShowDateTo(e.target.value);
                                if (e.target.value) setShowDay('');
                            }}
                            size="small"
                            fullWidth
                            disabled={!!showDay}
                            slotProps={{ inputLabel: { shrink: true } }}
                        />
                    </Box>

                    <Typography
                        variant="caption"
                        color="text.primary"
                        sx={{ mb: -1, textAlign: 'left', width: '100%', fontWeight: 600 }}
                    >
                        {t('reports.filters.bookingDateFrom').replace(' From', '')}
                    </Typography>
                    <TextField
                        type="date"
                        label={t('reports.filters.day')}
                        value={bookingDay}
                        onChange={(e) => pickBookingDay(e.target.value)}
                        size="small"
                        fullWidth
                        disabled={!!bookingDateFrom || !!bookingDateTo}
                        slotProps={{ inputLabel: { shrink: true } }}
                    />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField
                            type="date"
                            label={t('reports.filters.dateFrom')}
                            value={bookingDateFrom}
                            onChange={(e) => {
                                setBookingDateFrom(e.target.value);
                                if (e.target.value) setBookingDay('');
                            }}
                            size="small"
                            fullWidth
                            disabled={!!bookingDay}
                            slotProps={{ inputLabel: { shrink: true } }}
                        />
                        <TextField
                            type="date"
                            label={t('reports.filters.dateTo')}
                            value={bookingDateTo}
                            onChange={(e) => {
                                setBookingDateTo(e.target.value);
                                if (e.target.value) setBookingDay('');
                            }}
                            size="small"
                            fullWidth
                            disabled={!!bookingDay}
                            slotProps={{ inputLabel: { shrink: true } }}
                        />
                    </Box>
                    {(bookingDateFrom || bookingDateTo || bookingDay) && (
                        <RadioGroup
                            row
                            value={bookingDateMode}
                            onChange={(e) => setBookingDateMode(e.target.value)}
                            sx={{ mt: -0.5 }}
                        >
                            <FormControlLabel
                                value="created"
                                control={<Radio size="small" />}
                                label={t('reports.filters.bookingDateCreated', 'Booking Created')}
                                slotProps={{ typography: { variant: 'caption' } }}
                            />
                            <FormControlLabel
                                value="lastModified"
                                control={<Radio size="small" />}
                                label={t(
                                    'reports.filters.bookingDateLastModified',
                                    'Last Modified',
                                )}
                                slotProps={{ typography: { variant: 'caption' } }}
                            />
                        </RadioGroup>
                    )}

                    <Divider />

                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button variant="contained" onClick={handleApply} fullWidth size="small">
                            {t('common.apply')}
                        </Button>
                        <Tooltip title={t('reports.export')}>
                            <span>
                                <Button
                                    variant="outlined"
                                    onClick={handleExport}
                                    disabled={!hasData}
                                    size="small"
                                >
                                    <FileDownloadIcon />
                                </Button>
                            </span>
                        </Tooltip>
                    </Box>
                </Stack>
            </Paper>

            <Box sx={{ flexGrow: 1, minWidth: 0, width: '100%' }}>
                {isLoading && (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                        <CircularProgress />
                    </Box>
                )}
                {!isLoading && !hasData && (
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        minHeight={200}
                        sx={{ width: '100%' }}
                    >
                        <Typography variant="body1" color="text.secondary">
                            {t('reports.noData')}
                        </Typography>
                    </Box>
                )}
                {!isLoading && hasData && (
                    <AffiliateAgentReportTable
                        items={reportData.items}
                        totalSales={reportData.totalSales}
                        totalBookings={reportData.totalBookings}
                    />
                )}
            </Box>
        </Box>
    );
};

export default AgentPerformanceTab;
